export const formatWeeklyWarmapsQuestions = (questions) => {
  return questions.map((question) => {
    return {
      id: question.id,
      question: question.question,
      answer: question.answer,
    };
  });
};

export const formatWeek = (weekStart, weekEnd) => {
  if (!weekStart || !weekEnd) return "";
  const start = new Date(weekStart);
  const end = new Date(weekEnd);
  return `${start.getMonth() + 1} . ${start.getDate()} - ${end.getMonth() + 1} . ${end.getDate()}`;
};

export const calculateProgressPercentage = (currentStepIndex, totalSteps) => {
  if (totalSteps === 0) return 0;
  return ((currentStepIndex + 1) / totalSteps) * 100;
};

export const initializeAnswersFromAPI = (questions_by_steps) => {
  if (!questions_by_steps || !Array.isArray(questions_by_steps)) {
    return { answers: {}, submittedAnswers: {} };
  }

  const answers = {};
  const submittedAnswers = {};

  questions_by_steps.forEach((step) => {
    step.questions?.forEach((question) => {
      if (question.has_answered && question.answer?.answer_text != null) {
        const answerText = question.answer.answer_text;

        // For multiple answers, parse stringified JSON to array
        if (question.allow_multiple_answers) {
          try {
            if (typeof answerText === "string" && answerText.trim() !== "") {
              // Parse stringified JSON array to actual array
              if (
                answerText.trim().startsWith("[") &&
                answerText.trim().endsWith("]")
              ) {
                const parsed = JSON.parse(answerText);
                // Ensure it's an array
                if (Array.isArray(parsed)) {
                  answers[question.id] = parsed;
                  submittedAnswers[question.id] = parsed;
                } else {
                  // Single value, convert to array
                  answers[question.id] = [parsed];
                  submittedAnswers[question.id] = [parsed];
                }
              } else {
                // Single string value, convert to array
                answers[question.id] = [answerText];
                submittedAnswers[question.id] = [answerText];
              }
            } else {
              // Empty or invalid, use empty array
              answers[question.id] = [];
              submittedAnswers[question.id] = [];
            }
          } catch (e) {
            // If parsing fails, treat as single value array
            answers[question.id] = [answerText];
            submittedAnswers[question.id] = [answerText];
          }
        } else {
          // Single answer - store as string
          answers[question.id] = answerText;
          submittedAnswers[question.id] = answerText;
        }
      } else {
        // No answer yet - default values
        answers[question.id] = question.allow_multiple_answers ? [] : "";
        submittedAnswers[question.id] = question.allow_multiple_answers
          ? []
          : "";
      }
    });
  });

  return { answers, submittedAnswers };
};

export const normalizeAnswer = (answer, isMultiple = false) => {
  if (!answer) return isMultiple ? "[]" : "";

  if (isMultiple) {
    // Handle array answers - answer should already be an array
    try {
      let parsed = answer;
      // If it's a string, parse it (shouldn't happen but handle it)
      if (typeof answer === "string") {
        parsed = answer.trim().startsWith("[") ? JSON.parse(answer) : [answer];
      }
      // Ensure it's an array and sort for comparison
      const array = Array.isArray(parsed) ? parsed : [parsed];
      return JSON.stringify(array.map(String).sort());
    } catch {
      // If parsing fails, return empty array string
      return "[]";
    }
  }

  // Handle string answers
  if (typeof answer === "string") {
    return answer.trim();
  }
  return String(answer).trim();
};

export const areAllQuestionsAnswered = (questions, answers) => {
  if (!questions || questions.length === 0) return true;

  return questions.every((q) => {
    let answer = answers[q.id];

    // MULTIPLE ANSWERS
    if (q.allow_multiple_answers) {
      // Answer should already be an array, but handle string fallback
      if (typeof answer === "string" && answer.trim().startsWith("[")) {
        try {
          answer = JSON.parse(answer);
        } catch {
          return false;
        }
      }

      // Ensure it's an array and has at least one non-empty answer
      return (
        Array.isArray(answer) &&
        answer.length > 0 &&
        answer.some((item) => item != null && String(item).trim() !== "")
      );
    }

    // SINGLE ANSWER
    return typeof answer === "string" && answer.trim() !== "";
  });
};

export const hasAnswerChanged = (
  questions,
  currentAnswers,
  submittedAnswers,
) => {
  if (!questions || questions.length === 0) return false;

  return questions.some((question) => {
    const currentAnswer = currentAnswers[question.id];
    const submittedAnswer = submittedAnswers[question.id];

    const normalizedCurrent = normalizeAnswer(
      currentAnswer,
      question.allow_multiple_answers,
    );
    const normalizedSubmitted = normalizeAnswer(
      submittedAnswer,
      question.allow_multiple_answers,
    );

    return normalizedCurrent !== normalizedSubmitted;
  });
};

export const prepareStepAnswersForSubmission = (questions, answers) => {
  if (!questions || questions.length === 0) return [];

  return questions.map((question) => {
    let value = answers[question.id];

    if (question.allow_multiple_answers) {
      // Ensure it is always an array
      if (!Array.isArray(value)) {
        try {
          value = JSON.parse(value);
        } catch {
          value = value ? [value] : [];
        }
      }
    } else {
      // Ensure single answers are string
      value = value ?? "";
    }

    return {
      question_id: question.id,
      answer: value,
      create_hit_list: [18, 20].includes(question.id),
    };
  });
};

export const getCurrentStep = (
  questions_by_steps = [],
  current_question_id,
) => {
  const totalSteps = Array.isArray(questions_by_steps)
    ? questions_by_steps.length
    : 0;

  if (
    !Array.isArray(questions_by_steps) ||
    current_question_id === undefined ||
    current_question_id === null
  ) {
    return { totalSteps, currentStepIndex: 0, stepData: null };
  }

  const stepIndex = questions_by_steps.findIndex((step) => {
    if (!step || !Array.isArray(step.questions)) return false;
    return step.questions.some(
      (q) => q && q.id != null && q.id == current_question_id,
    );
  });

  if (stepIndex === -1) {
    // Find the first step with unanswered questions
    const firstUnansweredStepIndex = questions_by_steps.findIndex((step) => {
      if (
        !step ||
        !Array.isArray(step.questions) ||
        step.questions.length === 0
      )
        return false;
      return step.questions.some((q) => !q.has_answered);
    });

    // Before returning the first unanswered step, check if there's a step with no questions
    // (like Step6) that should be shown first because the previous step is completed
    if (firstUnansweredStepIndex !== -1) {
      // Check steps before the first unanswered step for display-only steps (no questions)
      for (let i = 0; i < firstUnansweredStepIndex; i++) {
        const step = questions_by_steps[i];
        if (
          step &&
          Array.isArray(step.questions) &&
          step.questions.length === 0
        ) {
          // Found a step with no questions - check if previous step is completed
          if (i > 0) {
            const prevStep = questions_by_steps[i - 1];
            if (
              prevStep &&
              Array.isArray(prevStep.questions) &&
              prevStep.questions.length > 0
            ) {
              const allPrevAnswered = prevStep.questions.every(
                (q) => q.has_answered,
              );
              if (allPrevAnswered) {
                // Previous step is completed, show this display-only step
                return {
                  totalSteps,
                  currentStepIndex: i,
                  stepData: step,
                };
              }
            }
          } else {
            // First step with no questions - show it
            return {
              totalSteps,
              currentStepIndex: i,
              stepData: step,
            };
          }
        }
      }

      return {
        totalSteps,
        currentStepIndex: firstUnansweredStepIndex,
        stepData: questions_by_steps[firstUnansweredStepIndex],
      };
    }

    // No unanswered questions found - find the last step with no questions that has a completed previous step
    for (let i = questions_by_steps.length - 1; i >= 0; i--) {
      const step = questions_by_steps[i];
      if (
        step &&
        Array.isArray(step.questions) &&
        step.questions.length === 0
      ) {
        if (i > 0) {
          const prevStep = questions_by_steps[i - 1];
          if (
            prevStep &&
            Array.isArray(prevStep.questions) &&
            prevStep.questions.length > 0
          ) {
            const allPrevAnswered = prevStep.questions.every(
              (q) => q.has_answered,
            );
            if (allPrevAnswered) {
              return {
                totalSteps,
                currentStepIndex: i,
                stepData: step,
              };
            }
          }
        } else {
          return {
            totalSteps,
            currentStepIndex: i,
            stepData: step,
          };
        }
      }
    }

    return {
      totalSteps,
      currentStepIndex: totalSteps - 1,
      stepData: questions_by_steps[totalSteps - 1],
    };
  }

  return {
    totalSteps,
    currentStepIndex: stepIndex,
    stepData: questions_by_steps[stepIndex],
  };
};

export const getStep2Data = (weekly_warmap_response) => {
  return {
    completedPmReflectionCount:
      weekly_warmap_response?.completed_pm_reflections_count ?? 0,
    completedAmReflectionCount:
      weekly_warmap_response?.completed_am_reflections_count ?? 0,
    total_submitted_journal_entries_count:
      weekly_warmap_response?.total_submitted_journal_entries_count ?? 0,
    lession_and_revelations:
      weekly_warmap_response?.lessons_and_revelations ?? [],
  };
};

export const getStep3Data = (weekly_warmap_response) => {
  return weekly_warmap_response?.completed_habits_count_for_week ?? {};
};

export const getStep4Data = (weekly_warmap_response) => {
  return {
    totalMitTasks:
      weekly_warmap_response?.mit_and_do_list_tasks?.stats?.total_mit_tasks ??
      0,
    totalMitAchieve:
      weekly_warmap_response?.mit_and_do_list_tasks?.stats
        ?.total_mit_achieved ?? 0,
    totalDoListDone:
      weekly_warmap_response?.mit_and_do_list_tasks?.stats
        ?.total_do_list_done ?? 0,
    tasks: {
      mitTasks: getMitTask(
        weekly_warmap_response?.mit_and_do_list_tasks?.tasks ?? [],
      ),
      doListTasks: getDoListTask(
        weekly_warmap_response?.mit_and_do_list_tasks?.tasks ?? [],
      ),
    },
  };
};

export const getStep5Data = (weekly_warmap_response) => {
  const wt = weekly_warmap_response?.weekly_targets ?? {};
  const sessionTotal = Number(wt.warmup_session_targets_total);
  const sessionCompleted = Number(wt.warmup_session_targets_completed);
  const useSessionChip = Number.isFinite(sessionTotal) && sessionTotal > 0;

  const rawTargets = wt.targets;
  const targets = Array.isArray(rawTargets)
    ? rawTargets
    : rawTargets && typeof rawTargets === "object"
      ? rawTargets
      : [];

  return {
    targets,
    totalGoals: wt.total_goals ?? 0,
    totalAchieved: useSessionChip
      ? sessionCompleted
      : (wt.completed_weekly_targets ?? 0),
    chipTotal: useSessionChip ? sessionTotal : (wt.total_goals ?? 0),
    warmupPeriod: weekly_warmap_response?.warmup_period ?? null,
  };
};

export function getStep6Data(weekly_warmap_response) {
  return {
    powerScoreStats: weekly_warmap_response?.weekly_power_score_stats ?? {},
    averageLifeScores: weekly_warmap_response?.average_life_scores ?? 0,
  };
}

export const getMitTask = (tasks = []) => {
  const mitTasks = tasks
    .filter((task) => task.level === "MIT List")
    .map((task) => ({
      title: task.title,
      created_at: task.created_at,
      completion_status: task.completion_status,
      level: task.level,
      isCompleted: task.isCompleted,
      dayOfWeek: task.day_of_week,
      due_date: task.due_date,
    }));

  return mitTasks;
};

export const getDoListTask = (tasks = []) => {
  const doListTasks = tasks
    .filter((task) => task.level === "Do List")
    .map((task) => ({
      title: task.title,
      created_at: task.created_at,
      completion_status: task.completion_status,
      level: task.level,
      isCompleted: task.isCompleted,
      dayOfWeek: task.day_of_week,
      due_date: task.due_date,
    }));

  return doListTasks;
};
